import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Utils
import cn from '../../../utils/cn';

// Components
import CalendarSidebar, {
  CalendarSidebarHeader,
  CalendarSidebarRail,
  CalendarSidebarRailDivider,
  CalendarSidebarRailLabel,
  CalendarSidebarSections,
  CalendarSidebarSection,
} from '../../molecules/CalendarSidebar/CalendarSidebar';
import FilterCalendarSidebarFilters, {
  FilterCalendarSidebarFiltersFooter,
} from '../FilterCalendarSidebarFilters/FilterCalendarSidebarFilters';
import ResourcesManagementPopover from '../ResourcesManagementPopover/ResourcesManagementPopover';
import Switch from '../../atoms/Switch/Switch';
import Tag from '../../atoms/Tag/Tag';
import Badge from '../../atoms/Badge/Badge';
import IconButton from '../../atoms/IconButton/IconButton';
import { Popover } from '../../../store/popover/PopoverContext';
import { PopoverContent, PopoverTrigger } from '../../atoms/Popover/Popover';

// Styles
import styles from './FilterCalendarSidebar.module.css';

/** @type {Array<'primary' | 'neutral' | 'success' | 'alert' | 'yellow' | 'orange' | 'green' | 'blue'>} */
const TAG_THEMES = ['primary', 'blue', 'green', 'alert', 'yellow', 'orange', 'success', 'neutral'];

/**
 * FilterCalendarSidebar - Persistent left sidebar for timeline calendar filters
 * @param {object} props
 * @param {'horizontal' | 'vertical'} [props.orientation]
 * @param {(orientation: 'horizontal' | 'vertical') => void} [props.onOrientationChange]
 * @param {Array<{ lib: string, options: Array<CalendarResource> }>} props.resourcesGroups
 * @param {(resourceId: string) => boolean} props.isResourceVisible
 * @param {(resourceId: string) => void} props.onToggleResource
 * @param {boolean} props.showResourcesModal
 * @param {() => void} props.onOpenResourcesModal
 * @param {() => void} props.onCloseResourcesModal
 * @param {() => void} [props.onApplyResourcesModal]
 * @param {Array<CalendarResource>} props.allResources
 * @param {string | null} props.resourceGroupField
 * @param {(groupValue: string) => 'all-visible' | 'all-hidden' | 'mixed' | null} props.getGroupVisibilityState
 * @param {(groupValue: string, groupField: string) => void} [props.onToggleGroup]
 * @param {Array<{ lib: string, options: Array<CalendarResource> }>} [props.popoverResourcesGroups]
 * @param {(resourceId: string) => boolean} [props.popoverIsResourceVisible]
 * @param {(resourceId: string) => void} [props.popoverOnToggleResource]
 * @param {(groupValue: string) => 'all-visible' | 'all-hidden' | 'mixed' | null} [props.popoverGetGroupVisibilityState]
 * @param {(groupValue: string, groupField: string) => void} [props.popoverOnToggleGroup]
 * @param {string | null} [props.popoverResourceGroupField]
 * @param {string} props.searchResources
 * @param {(search: string) => void} props.setSearchResources
 * @param {ReturnType<typeof import('../../../domain/calendarSidebarFilters/useCalendarSidebarFilters').default>} [props.sidebarFilters]
 * @param {(data: Array<ApiManagerFilterValues>) => void} [props.onSidebarFiltersSubmit]
 * @param {boolean} [props.isSidebarFiltersLoading]
 * @param {boolean} [props.isSidebarFiltersSubmitting]
 * @param {boolean} [props.isCollapsed=false]
 * @param {() => void} [props.onToggleCollapse]
 * @param {number} [props.activeFiltersCount=0]
 * @param {number} [props.visibleResourcesCount=0]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
function FilterCalendarSidebar({
  orientation,
  onOrientationChange,
  resourcesGroups,
  isResourceVisible,
  onToggleResource,
  showResourcesModal,
  onOpenResourcesModal,
  onCloseResourcesModal,
  onApplyResourcesModal,
  allResources,
  resourceGroupField,
  getGroupVisibilityState,
  onToggleGroup,
  popoverResourcesGroups,
  popoverIsResourceVisible,
  popoverOnToggleResource,
  popoverGetGroupVisibilityState,
  popoverOnToggleGroup,
  popoverResourceGroupField,
  searchResources,
  setSearchResources,
  sidebarFilters,
  onSidebarFiltersSubmit,
  isSidebarFiltersLoading = false,
  isSidebarFiltersSubmitting = false,
  isCollapsed = false,
  onToggleCollapse,
  activeFiltersCount = 0,
  visibleResourcesCount = 0,
  className = '',
}) {
  const { t } = useTranslation();
  const [activeResourcesGroupLib, setActiveResourcesGroupLib] = useState(/** @type {string | null} */ (null));

  const closeResourcesPopover = () => {
    setActiveResourcesGroupLib(null);
    onCloseResourcesModal();
  };

  const handleResourcesPopoverOpenChange = (/** @type {string} */ groupLib, /** @type {boolean} */ open) => {
    if (open) {
      setActiveResourcesGroupLib(groupLib);
      onOpenResourcesModal();
      return;
    }
    closeResourcesPopover();
  };

  const renderResourcesManagement = () => (
    <ResourcesManagementPopover
      allResources={allResources}
      isResourceVisible={popoverIsResourceVisible ?? isResourceVisible}
      onToggleResource={popoverOnToggleResource ?? onToggleResource}
      resourcesGroups={popoverResourcesGroups ?? resourcesGroups}
      resourceGroupField={popoverResourceGroupField ?? resourceGroupField}
      getGroupVisibilityState={popoverGetGroupVisibilityState ?? getGroupVisibilityState}
      onToggleGroup={popoverOnToggleGroup ?? onToggleGroup}
      searchResources={searchResources}
      setSearchResources={setSearchResources}
      onCancel={closeResourcesPopover}
      onApply={() => {
        onApplyResourcesModal?.();
        closeResourcesPopover();
      }}
    />
  );

  const collapseToggleTitle = isCollapsed
    ? t('calendar.sidebar.expand')
    : t('calendar.sidebar.collapse');

  const handleRailReset = () => {
    sidebarFilters?.handleResetSidebarFilters();
    onToggleCollapse?.();
  };

  return (
    <CalendarSidebar
      className={cn([styles.root, className])}
      isCollapsed={isCollapsed}
    >
      {onToggleCollapse ? (
        <CalendarSidebarHeader>
          <IconButton
            icon="chevron-left-range"
            size="small"
            color="transparent"
            title={collapseToggleTitle}
            onClick={onToggleCollapse}
            aria-expanded={!isCollapsed}
            className={cn([
              styles.toggle,
              isCollapsed && styles['toggle-collapsed'],
            ])}
          />
        </CalendarSidebarHeader>
      ) : null}
      {onToggleCollapse ? (
        <CalendarSidebarRail>
          <CalendarSidebarRailDivider />
          <div className={styles['rail-action']}>
            <IconButton
              icon="filter"
              size="small"
              color="transparent"
              title={t('calendar.sidebar.expand')}
              onClick={onToggleCollapse}
            />
            {activeFiltersCount > 0 ? (
              <span className={styles['rail-badge']}>
                <Badge value={activeFiltersCount} />
              </span>
            ) : null}
          </div>
          <div className={styles['rail-action']}>
            <IconButton
              icon="task"
              size="small"
              color="transparent"
              title={t('calendar.sidebar.resources')}
              onClick={onToggleCollapse}
            />
            {visibleResourcesCount > 0 ? (
              <span className={styles['rail-badge']}>
                <Badge value={visibleResourcesCount} />
              </span>
            ) : null}
          </div>
          <IconButton
            icon="refresh"
            size="small"
            color="transparent"
            title={t('calendar.sidebar.filters.reset')}
            onClick={handleRailReset}
            disabled={activeFiltersCount === 0}
          />
          <CalendarSidebarRailLabel>
            {t('calendar.sidebar.railLabel')}
          </CalendarSidebarRailLabel>
        </CalendarSidebarRail>
      ) : null}
      <CalendarSidebarSections>
        {onOrientationChange ? (
          <CalendarSidebarSection label={t('calendar.sidebar.appearance')}>
            <Switch
              id="calendar-sidebar-orientation"
              name="calendar-sidebar-orientation"
              checked={orientation === 'vertical'}
              onChange={(checked) => onOrientationChange(checked ? 'vertical' : 'horizontal')}
              label={t('calendar.sidebar.list')}
              size="small"
            />
          </CalendarSidebarSection>
        ) : null}

        {resourcesGroups.map((group) => {
          const visibleResources = group.options.filter((resource) => isResourceVisible(resource.id));
          const isGroupPopoverOpen = showResourcesModal && activeResourcesGroupLib === group.lib;

          return (
            <CalendarSidebarSection
              key={group.lib}
              label={group.lib}
              action={(
                <Popover
                  open={isGroupPopoverOpen}
                  onOpenChange={(open) => handleResourcesPopoverOpenChange(group.lib, open)}
                  placement="bottom-start"
                >
                  <PopoverTrigger asChild>
                    <IconButton
                      icon="add"
                      size="small"
                      color="transparent"
                      title={t('calendar.sidebar.manageResources')}
                      onClick={() => handleResourcesPopoverOpenChange(group.lib, true)}
                    />
                  </PopoverTrigger>
                  <PopoverContent>
                    {renderResourcesManagement()}
                  </PopoverContent>
                </Popover>
              )}
            >
              <div className={styles['tags-list']}>
                {visibleResources.map((resource, index) => (
                  <Tag
                    key={resource.id}
                    theme={TAG_THEMES[index % TAG_THEMES.length]}
                    onRemove={() => onToggleResource(resource.id)}
                  >
                    {resource.title}
                  </Tag>
                ))}
              </div>
            </CalendarSidebarSection>
          );
        })}

        {sidebarFilters && onSidebarFiltersSubmit ? (
          <FilterCalendarSidebarFilters
            sidebarFilters={sidebarFilters}
            onSubmit={onSidebarFiltersSubmit}
          />
        ) : null}
      </CalendarSidebarSections>
      {!isCollapsed && sidebarFilters && onSidebarFiltersSubmit ? (
        <FilterCalendarSidebarFiltersFooter
          sidebarFilters={sidebarFilters}
          isLoading={isSidebarFiltersLoading}
          isSubmitting={isSidebarFiltersSubmitting}
        />
      ) : null}
    </CalendarSidebar>
  );
}

export default FilterCalendarSidebar;
